import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/global.css';
import './styles/splashScreen.css';
import configPromise from './config/config-loader';
import { ConfigProvider } from './config/ConfigContext';
import { initializeBranding } from './utils/pwaBrandingInitializer';

const root = ReactDOM.createRoot(document.getElementById('root'));

// Function to update splash screen with branding
function updateSplashScreen(config) {
  const splashScreen = document.getElementById('splash-screen');
  const splashLogo = document.getElementById('splash-logo');
  const splashText = document.querySelector('.splash-text');
  
  if (splashScreen) {
    // Update colors using CSS custom properties
    splashScreen.style.setProperty('--splash-bg-color', config.colors?.background || '#ffffff');
    splashScreen.style.setProperty('--splash-primary-color', config.colors?.primary || '#001f3f');
    splashScreen.style.setProperty('--splash-secondary-color', config.colors?.secondary || config.colors?.primary || '#0d6efd');
    splashScreen.style.setProperty('--splash-text-color', config.colors?.text || '#333');
    
    // Splash logo selection (in priority order):
    //   1. splash_logo_url — dedicated splash asset (richer/decorative variant)
    //   2. logo_url        — the standard app logo (used for PWA icons too)
    //   3. config.logo     — legacy fallback
    // Rationale: the splash screen is shown briefly and can afford a more
    // visually impactful logo; the PWA app icon needs to stay clean for
    // small-size readability. We let centers configure them separately.
    const splashSrc = config.splash_logo_url || config.logo_url || config.logo;
    if (splashSrc && splashLogo) {
      splashLogo.src = splashSrc;
      splashLogo.style.visibility = 'visible';
      splashLogo.onerror = function() {
        // Fall back to the app logo if the splash asset fails to load
        if (this.src !== config.logo_url && config.logo_url) {
          this.src = config.logo_url;
        } else {
          this.style.visibility = 'hidden';
        }
      };
    }
    
    // Update loading text with center name
    if (splashText) {
      splashText.textContent = `Loading ${config.name || 'Penta University'}`;
    }
    
    // Add center name display
    const centerNameDiv = document.createElement('div');
    centerNameDiv.className = 'splash-center-name';
    centerNameDiv.textContent = config.name || 'Penta University';
    splashScreen.insertBefore(centerNameDiv, splashScreen.querySelector('.splash-progress'));
  }
}

// Function to hide splash screen with fade animation
function hideSplashScreen() {
  const splashScreen = document.getElementById('splash-screen');
  if (splashScreen) {
    // Add fade-out class
    splashScreen.classList.add('fade-out');
    
    // Remove from DOM after animation completes
    setTimeout(() => {
      splashScreen.remove();
    }, 500);
  }
}

// Load config first, then render app with ConfigProvider
configPromise.then(config => {
  console.log('Center configuration loaded:', config.name);
  
  // Update splash screen with branding
  updateSplashScreen(config);
  
  // Initialize PWA branding BEFORE React renders
  // This ensures iOS sees the correct meta tags when user adds to home screen
  initializeBranding(config);
  
  // Small delay to show branded splash screen
  setTimeout(() => {
    root.render(
      <React.StrictMode>
        <ConfigProvider>
          <App />
        </ConfigProvider>
      </React.StrictMode>
    );
    
    // Hide splash screen after React mounts
    setTimeout(hideSplashScreen, 300);
  }, 500);
}).catch(error => {
  console.error('Failed to load configuration:', error);
  
  // Initialize with default branding
  const defaultConfig = {
    id: 'default',
    name: 'Penta University',
    short_name: 'Penta Uni',
    colors: { primary: '#0d6efd' }
  };
  
  // Update splash screen with default branding
  updateSplashScreen(defaultConfig);
  initializeBranding(defaultConfig);
  
  // Render with default config
  setTimeout(() => {
    root.render(
      <React.StrictMode>
        <ConfigProvider>
          <App />
        </ConfigProvider>
      </React.StrictMode>
    );
    
    // Hide splash screen after React mounts
    setTimeout(hideSplashScreen, 300);
  }, 500);
});

// Register service worker for PWA functionality - moved before window.load for faster deep linking
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  // Register immediately for faster link capture
  navigator.serviceWorker.register('/service-worker-enhanced.js', { scope: '/' })
    .then(registration => {
      console.log('Enhanced Service Worker registered successfully:', registration.scope);
      console.log('[SW] Active state:', registration.active ? 'active' : 'not active');
      console.log('[SW] Waiting state:', registration.waiting ? 'waiting' : 'not waiting');
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker available, prompt for update
              console.log('New content available, please refresh.');
            }
          });
        }
      });
      
      // Check if there's a waiting worker and prompt for update
      if (registration.waiting) {
        console.log('[SW] New version waiting, prompting update...');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    })
    .catch(error => {
      console.error('Service Worker registration failed:', error);
      console.error('[SW] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    });
}
